import React, { useCallback, useEffect, useMemo, useState } from "react"
import Popup from "../../component/popup/popup"
import InputText from "../../component/inputText/inputText"
import PhoneInput from "../../component/phoneInput/phoneInput"
import Toggle from "../../component/toggle/Toggle"
import Button from "../../component/button/button"
import Grid from "@mui/material/Grid"
import { AgencyData, AgentData, OfficeData } from "../../models"
import { IUpsertAgencyApiPayload } from "../../models/axiosModel"
import { useSnackbar } from "../../context/snackbarContext"
import { agent } from "../../apiClient/api"
import IconsColor from "../../util/iconsColor"
import AgencyWizardStepper, { WizardStepConfig } from "./AgencyWizardStepper"
import styles from "../../asset/styles/agents/addAgencyWizardPopup.module.scss"

/** Set to false to allow stepper jumps without validating intermediate steps. */
const VALIDATE_ON_STEPPER_CLICK = true

const WIZARD_STEPS: WizardStepConfig[] = [
    { id: 1, label: "Agency Details" },
    { id: 2, label: "Offices" },
    { id: 3, label: "Agents" },
]

export type AgencyWizardMode = "createAgency" | "addAgents" | "editAgent" | "editAgency"

type OfficeFormData = {
    office_id?: string | null
    office_name: string
    city_name: string
    address: string
    contact_number: string
    email: string
    flag_active?: boolean
}

type AgentFormData = {
    agent_id?: string | null
    agent_name: string
    email: string
    contact_number: string
    flag_active?: boolean
}

type OfficeFieldKey = "office_name" | "city_name" | "address" | "contact_number" | "email"

type StepErrors = {
    agencyName?: string
    contactPersonName?: string
    phoneNumber?: string
    email?: string
    officeErrors?: Partial<Record<OfficeFieldKey, string>>[]
    agentsErrors?: Partial<Record<keyof AgentFormData, string>>[][]
}

type AddAgencyWizardPopupProps = {
    isOpen: boolean
    onClose: () => void
    onSuccess?: () => void
    initialStep?: 1 | 2 | 3
    wizardMode?: AgencyWizardMode
    agency?: AgencyData | null
    editingAgent?: AgentData | null
}

const OFFICE_FIELDS: OfficeFieldKey[] = ["office_name", "city_name", "address", "contact_number", "email"]

const emptyOffice = (): OfficeFormData => ({
    office_id: null,
    office_name: "",
    city_name: "",
    address: "",
    contact_number: "",
    email: "",
})

const emptyAgent = (): AgentFormData => ({
    agent_id: null,
    agent_name: "",
    email: "",
    contact_number: "",
})

const DEFAULT_PHONE_COUNTRY_CODE = "+91"

/**
 * Brings legacy phone values (spaces, missing "+", no country code) into the "+<digits>" shape
 * PhoneInput writes and isValidPhone expects. A bare 10-digit number is assumed to be Indian.
 */
const normalizePhoneFromApi = (value?: string | null): string => {
    const trimmed = value?.trim() ?? ""
    if (!trimmed) return ""
    const digitsOnly = trimmed.replace(/\D/g, "")
    if (!digitsOnly) return ""
    if (trimmed.startsWith("+")) return `+${digitsOnly}`
    if (digitsOnly.length === 10) return `${DEFAULT_PHONE_COUNTRY_CODE}${digitsOnly}`
    return `+${digitsOnly}`
}

const mapAgentFromApi = (agentRow: AgentData): AgentFormData => ({
    agent_id: agentRow.agent_id,
    agent_name: agentRow.agent_name ?? "",
    email: agentRow.email ?? "",
    contact_number: normalizePhoneFromApi(agentRow.contact_number),
    flag_active: agentRow.flag_active,
})

const isExistingAgentRow = (agentRow: AgentFormData): boolean => Boolean(agentRow.agent_id)

const countDisplayAgents = (officeAgents: AgentFormData[]): number =>
    officeAgents.filter((row) => isExistingAgentRow(row) || !isAgentRowEmpty(row)).length

const buildPrimaryAgentFromStep1 = (
    contactPersonName: string,
    office: OfficeFormData,
): AgentFormData => ({
    agent_name: contactPersonName.trim(),
    email: office.email?.trim() ?? "",
    contact_number: office.contact_number?.trim() ?? "",
})

const isValidEmail = (value: string): boolean => /\S+@\S+\.\S+/.test(value.trim())

const isValidPhone = (value: string): boolean => /^\+[0-9]{10,15}$/.test(value.trim())

const getOfficeFillState = (office: OfficeFormData): "empty" | "partial" | "complete" => {
    const filledCount = OFFICE_FIELDS.filter((field) => office[field]?.trim()).length
    if (filledCount === 0) return "empty"
    if (filledCount === OFFICE_FIELDS.length) return "complete"
    return "partial"
}

const mapOfficeFromApi = (office: OfficeData): OfficeFormData => ({
    office_id: office.office_id ?? null,
    office_name: office.office_name ?? "",
    city_name: office.city_name ?? "",
    address: office.address ?? "",
    contact_number: normalizePhoneFromApi(office.contact_number),
    email: office.email ?? "",
    flag_active: office.flag_active ?? true,
})

const isAgentRowEmpty = (agentRow: AgentFormData): boolean =>
    !agentRow.agent_name?.trim() && !agentRow.email?.trim() && !agentRow.contact_number?.trim()

const isAgentContactFieldsRequired = (agentIndex: number): boolean => agentIndex === 0

/** Upsert soft-deletes anything left out, so every existing office/agent is always sent. */
const buildUpsertPayload = (params: {
    agencyId: string
    agencyName: string
    contactPersonName: string
    phoneNumber: string
    email: string
    flagActive: boolean
    offices: OfficeFormData[]
    agents: AgentFormData[][]
}): IUpsertAgencyApiPayload => ({
    agency_id: params.agencyId,
    agency_name: params.agencyName.trim(),
    contact_person_name: params.contactPersonName.trim(),
    phone_number: params.offices[0]?.contact_number?.trim() || params.phoneNumber.trim(),
    email: params.offices[0]?.email?.trim() || params.email.trim(),
    // TODO: confirm with backend whether upsert accepts agency-level flag_active
    flag_active: params.flagActive,
    office: params.offices
        .map((office, officeIndex) => ({ office, officeAgents: params.agents[officeIndex] ?? [] }))
        .filter(({ office }) => Boolean(office.office_id) || getOfficeFillState(office) !== "empty")
        .map(({ office, officeAgents }) => ({
            office_id: office.office_id || null,
            office_name: office.office_name.trim(),
            city_name: office.city_name.trim(),
            address: office.address.trim(),
            contact_number: office.contact_number.trim() || null,
            email: office.email.trim() || null,
            flag_active: office.flag_active ?? true,
            agents: officeAgents
                .filter((agentRow) => isExistingAgentRow(agentRow) || !isAgentRowEmpty(agentRow))
                .map((agentRow) => ({
                    agent_id: agentRow.agent_id || null,
                    agent_name: agentRow.agent_name.trim(),
                    email: agentRow.email.trim() || null,
                    contact_number: agentRow.contact_number.trim() || null,
                    flag_active: agentRow.flag_active ?? true,
                })),
        })),
})

const validateAgentRowFields = (
    agentRow: AgentFormData,
    agentIndex: number,
    isPrimaryAgent = false,
): Partial<Record<keyof AgentFormData, string>> => {
    const rowErr: Partial<Record<keyof AgentFormData, string>> = {}
    const requireContactFields = isAgentContactFieldsRequired(agentIndex) || isPrimaryAgent

    if (!agentRow.agent_name?.trim()) rowErr.agent_name = "Agent Name is required"
    if (requireContactFields) {
        if (!agentRow.email?.trim()) rowErr.email = "Email ID is required"
        else if (!isValidEmail(agentRow.email)) rowErr.email = "Invalid email format"
        if (!agentRow.contact_number?.trim()) rowErr.contact_number = "Phone Number is required"
        else if (!isValidPhone(agentRow.contact_number)) rowErr.contact_number = "Invalid phone number"
    } else {
        if (agentRow.email?.trim() && !isValidEmail(agentRow.email)) rowErr.email = "Invalid email format"
        if (agentRow.contact_number?.trim() && !isValidPhone(agentRow.contact_number)) {
            rowErr.contact_number = "Invalid phone number"
        }
    }
    return rowErr
}

const AddAgencyWizardPopup: React.FC<AddAgencyWizardPopupProps> = ({
    isOpen,
    onClose,
    onSuccess,
    initialStep = 1,
    wizardMode = "createAgency",
    agency = null,
    editingAgent = null,
}) => {
    const { showError, showSuccess } = useSnackbar()
    const isAddAgentsMode = wizardMode === "addAgents"
    const isEditAgentMode = wizardMode === "editAgent"
    const isEditAgencyMode = wizardMode === "editAgency"
    const isAgencyAgentFlow = isAddAgentsMode || isEditAgentMode
    const isExistingAgencyFlow = isAgencyAgentFlow || isEditAgencyMode
    const isEditFlow = isEditAgencyMode || isEditAgentMode

    const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1)
    const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set())
    const [agencyName, setAgencyName] = useState("")
    const [contactPersonName, setContactPersonName] = useState("")
    const [phoneNumber, setPhoneNumber] = useState("")
    const [email, setEmail] = useState("")
    const [flagActive, setFlagActive] = useState(true)
    const [offices, setOffices] = useState<OfficeFormData[]>([emptyOffice()])
    const [agents, setAgents] = useState<AgentFormData[][]>([[emptyAgent()]])
    // First agent returned by AgentListAPI; same person as the agency contact (name/email/phone).
    const [primaryAgentId, setPrimaryAgentId] = useState<string | null>(null)
    const [expandedOfficeIndexes, setExpandedOfficeIndexes] = useState<Set<number>>(new Set([0]))
    const [errors, setErrors] = useState<StepErrors>({})
    const [isSaving, setIsSaving] = useState(false)
    const [isLoadingAgencyData, setIsLoadingAgencyData] = useState(false)

    const resetForm = useCallback(() => {
        setPrimaryAgentId(null)
        if (isAgencyAgentFlow) {
            setCurrentStep(3)
            setCompletedSteps(new Set([1, 2]))
            setAgencyName(agency?.agency_name ?? "")
            setContactPersonName(agency?.contact_person_name ?? "")
            setPhoneNumber(agency?.phone_number ?? "")
            setEmail(agency?.email ?? "")
            setFlagActive(agency?.flag_active ?? true)
            setOffices([])
            setAgents([])
            setExpandedOfficeIndexes(new Set())
            setErrors({})
            setIsSaving(false)
            return
        }

        if (isEditAgencyMode) {
            setCurrentStep(1)
            setCompletedSteps(new Set())
            setAgencyName(agency?.agency_name ?? "")
            setContactPersonName(agency?.contact_person_name ?? "")
            setPhoneNumber(agency?.phone_number ?? "")
            setEmail(agency?.email ?? "")
            setFlagActive(agency?.flag_active ?? true)
            setOffices([])
            setAgents([])
            setExpandedOfficeIndexes(new Set([0]))
            setErrors({})
            setIsSaving(false)
            return
        }

        setCurrentStep(initialStep)
        setCompletedSteps(new Set())
        setAgencyName("")
        setContactPersonName("")
        setPhoneNumber("")
        setEmail("")
        setFlagActive(true)
        setOffices([emptyOffice()])
        setAgents([[emptyAgent()]])
        setExpandedOfficeIndexes(new Set([0]))
        setErrors({})
        setIsSaving(false)
    }, [initialStep, isAgencyAgentFlow, isEditAgencyMode, agency])

    useEffect(() => {
        if (isOpen) {
            resetForm()
        }
    }, [isOpen, resetForm])

    const isPrimaryAgentRow = (agentRow?: AgentFormData): boolean =>
        Boolean(primaryAgentId) && agentRow?.agent_id === primaryAgentId

    /** Copies agency contact changes (step 1 / Office 1) onto the first agent. */
    const patchPrimaryAgent = (patch: Partial<AgentFormData>) => {
        if (!isEditFlow || !primaryAgentId) return
        setAgents((prev) =>
            prev.map((officeAgents) =>
                officeAgents.map((agentRow) =>
                    agentRow.agent_id === primaryAgentId ? { ...agentRow, ...patch } : agentRow,
                ),
            ),
        )
    }

    useEffect(() => {
        if (!isOpen || !isExistingAgencyFlow || !agency?.agency_id) return

        const loadAgencyData = async () => {
            setIsLoadingAgencyData(true)
            try {
                const [officeResponse, agentResponse, agencyResponse] = await Promise.all([
                    agent.OfficeListAPI.get(agency.agency_id, true),
                    agent.AgentListAPI.get(agency.agency_id, true),
                    agent.AgencyDetailAPI.get(agency.agency_id, true),
                ])

                const agencyDetails: AgencyData =
                    agencyResponse?.status === true && agencyResponse?.data?.[0]
                        ? agencyResponse.data[0]
                        : agency

                const officeList: OfficeData[] =
                    officeResponse?.status === true
                        ? (officeResponse?.data ?? agencyDetails.office ?? [])
                        : (agencyDetails.office ?? [])

                const agentList: AgentData[] =
                    agentResponse?.status === true ? (agentResponse?.data ?? []) : []

                if (officeList.length === 0) {
                    showError("No offices found for this agency")
                    return
                }

                setAgencyName(agencyDetails.agency_name ?? "")
                setContactPersonName(agencyDetails.contact_person_name ?? "")
                setPhoneNumber(normalizePhoneFromApi(agencyDetails.phone_number))
                setEmail(agencyDetails.email ?? "")
                setFlagActive(agencyDetails.flag_active ?? true)

                const mappedOffices = officeList.map(mapOfficeFromApi)
                const agentsByOffice = mappedOffices.map((office) => {
                    const existingAgents = agentList
                        .filter((row) => row.office_id === office.office_id)
                        .map(mapAgentFromApi)
                    if (isEditAgentMode) return existingAgents
                    return [...existingAgents, emptyAgent()]
                })

                const editingOfficeIndex = editingAgent
                    ? mappedOffices.findIndex((office) => office.office_id === editingAgent.office_id)
                    : 0

                setOffices(mappedOffices)
                setAgents(agentsByOffice)
                setPrimaryAgentId(agentList[0]?.agent_id ?? null)
                setExpandedOfficeIndexes(
                    isEditAgencyMode
                        ? new Set([0])
                        : new Set([editingOfficeIndex >= 0 ? editingOfficeIndex : 0]),
                )
            } catch (err) {
                console.error("Load agency data error", err)
                showError("Failed to load agency data")
            } finally {
                setIsLoadingAgencyData(false)
            }
        }

        void loadAgencyData()
    }, [isOpen, isExistingAgencyFlow, isEditAgencyMode, isEditAgentMode, agency, editingAgent, showError])

    const activeOfficeIndexes = useMemo(
        () =>
            offices.reduce<number[]>((acc, office, index) => {
                if (isExistingAgencyFlow || index === 0 || getOfficeFillState(office) === "complete") {
                    acc.push(index)
                }
                return acc
            }, []),
        [offices, isExistingAgencyFlow],
    )

    const ensureAgentsForActiveOffices = useCallback(() => {
        setAgents((prev) => {
            const updated = prev.map((officeAgents) => [...officeAgents])

            activeOfficeIndexes.forEach((officeIndex) => {
                if (isAddAgentsMode || isEditAgencyMode) {
                    if (!updated[officeIndex]?.length) {
                        updated[officeIndex] = [emptyAgent()]
                        return
                    }

                    const hasEmptyNewRow = updated[officeIndex].some(
                        (row) => !isExistingAgentRow(row) && isAgentRowEmpty(row),
                    )
                    if (!hasEmptyNewRow) {
                        updated[officeIndex] = [...updated[officeIndex], emptyAgent()]
                    }
                } else if (officeIndex === 0) {
                    const primaryAgent = buildPrimaryAgentFromStep1(contactPersonName, offices[0])
                    const additionalAgents = updated[0]?.length > 1 ? updated[0].slice(1) : []
                    updated[0] = [primaryAgent, ...additionalAgents]
                } else if (!updated[officeIndex] || updated[officeIndex].length === 0) {
                    updated[officeIndex] = [emptyAgent()]
                }
            })

            return updated
        })
    }, [activeOfficeIndexes, contactPersonName, offices, isAddAgentsMode, isEditAgencyMode])

    const totalAgentCount = useMemo(() => {
        if (isExistingAgencyFlow) {
            return activeOfficeIndexes.reduce(
                (sum, index) => sum + countDisplayAgents(agents[index] ?? []),
                0,
            )
        }
        return activeOfficeIndexes.reduce((sum, index) => sum + (agents[index]?.length ?? 0), 0)
    }, [activeOfficeIndexes, agents, isExistingAgencyFlow])

    const enterStep3 = useCallback(() => {
        ensureAgentsForActiveOffices()
        setExpandedOfficeIndexes(new Set(activeOfficeIndexes))
    }, [activeOfficeIndexes, ensureAgentsForActiveOffices])

    useEffect(() => {
        if (currentStep !== 3 || isAgencyAgentFlow) return
        enterStep3()
    }, [currentStep, enterStep3, isAgencyAgentFlow])

    const validateOfficeFields = (office: OfficeFormData, requireAll: boolean): Partial<Record<OfficeFieldKey, string>> => {
        const err: Partial<Record<OfficeFieldKey, string>> = {}
        const fillState = getOfficeFillState(office)

        if (requireAll || fillState === "complete" || fillState === "partial") {
            if (!office.office_name?.trim()) err.office_name = "Office Name is required"
            if (!office.city_name?.trim()) err.city_name = "City is required"
            if (!office.address?.trim()) err.address = "Address is required"
            if (!office.contact_number?.trim()) err.contact_number = "Contact Number is required"
            else if (!isValidPhone(office.contact_number)) err.contact_number = "Invalid contact number"
            if (!office.email?.trim()) err.email = "Email ID is required"
            else if (!isValidEmail(office.email)) err.email = "Invalid email format"
        }

        // Optional offices only: a required office already gets a specific error per blank field,
        // so its valid fields (e.g. phone/email after a CSV import) must not be flagged.
        if (fillState === "partial" && !requireAll) {
            OFFICE_FIELDS.forEach((field) => {
                if (!err[field]) err[field] = "All office fields are required once any field is filled"
            })
        }

        return err
    }

    const validateStep1 = (): boolean => {
        const next: StepErrors = {}
        if (!agencyName?.trim()) next.agencyName = "Agency Name is required"
        if (!contactPersonName?.trim()) next.contactPersonName = "Contact Person Name is required"
        if (!offices[0].contact_number?.trim()) next.phoneNumber = "Phone Number is required"
        else if (!isValidPhone(offices[0].contact_number)) next.phoneNumber = "Invalid phone number format"
        if (!offices[0].email?.trim()) next.email = "Email ID is required"
        else if (!isValidEmail(offices[0].email)) next.email = "Invalid email format"

        const officeForValidation: OfficeFormData =
            !isEditAgencyMode && agencyName.trim()
                ? { ...offices[0], office_name: agencyName.trim() }
                : offices[0]

        const officeErr = validateOfficeFields(officeForValidation, true)
        if (Object.keys(officeErr).length > 0) {
            next.officeErrors = [officeErr]
        }

        setErrors(next)
        return Object.keys(next).length === 0
    }

    const validateStep2 = (): boolean => {
        const officeErrors = offices.map((office, index) => {
            if (index === 0) return validateOfficeFields(office, true)
            const fillState = getOfficeFillState(office)
            if (fillState === "empty") return {}
            return validateOfficeFields(office, false)
        })

        const hasOfficeErrors = officeErrors.some((entry) => Object.keys(entry).length > 0)
        setErrors(hasOfficeErrors ? { officeErrors } : {})
        return !hasOfficeErrors
    }

    const validateStep3 = (): boolean => {
        const agentsErrors: Partial<Record<keyof AgentFormData, string>>[][] = []
        let newAgentCount = 0

        if (isEditAgentMode) {
            if (!editingAgent?.agent_id) {
                showError("Agent not found")
                return false
            }
            let agentFound = false
            activeOfficeIndexes.forEach((officeIndex) => {
                const officeAgents = agents[officeIndex] ?? []
                agentsErrors[officeIndex] = officeAgents.map((agentRow, agentIndex) => {
                    if (agentRow.agent_id !== editingAgent.agent_id) return {}

                    agentFound = true
                    return validateAgentRowFields(agentRow, agentIndex, isPrimaryAgentRow(agentRow))
                })
            })

            if (!agentFound) {
                showError("Agent not found")
                return false
            }

            const hasAgentErrors = agentsErrors.some(
                (officeAgentErrors) =>
                    officeAgentErrors?.some((rowErr) => rowErr && Object.keys(rowErr).length > 0),
            )
            setErrors(hasAgentErrors ? { agentsErrors } : {})
            return !hasAgentErrors
        }

        activeOfficeIndexes.forEach((officeIndex) => {
            const officeAgents = agents[officeIndex] ?? []

            if (isAddAgentsMode || isEditAgencyMode) {
                agentsErrors[officeIndex] = officeAgents.map((agentRow, agentIndex) => {
                    // Existing rows are always validated: upsert would save a cleared row as-is.
                    if (!isExistingAgentRow(agentRow) && isAgentRowEmpty(agentRow)) return {}
                    if (isAddAgentsMode && isExistingAgentRow(agentRow)) return {}

                    if (isAddAgentsMode) newAgentCount += 1

                    return validateAgentRowFields(agentRow, agentIndex, isPrimaryAgentRow(agentRow))
                })
                return
            }

            if (officeAgents.length === 0) {
                agentsErrors[officeIndex] = [{ agent_name: "At least one agent is required per office" }]
                return
            }

            agentsErrors[officeIndex] = officeAgents.map((agentRow, agentIndex) =>
                validateAgentRowFields(agentRow, agentIndex),
            )
        })

        if (isAddAgentsMode && newAgentCount === 0) {
            showError("Please add at least one new agent")
            return false
        }

        const hasAgentErrors = agentsErrors.some(
            (officeAgentErrors) =>
                officeAgentErrors?.some((rowErr) => rowErr && Object.keys(rowErr).length > 0),
        )

        setErrors(hasAgentErrors ? { agentsErrors } : {})
        return !hasAgentErrors
    }

    const validateUpToStep = (targetStep: number): boolean => {
        if (targetStep <= 1) return true
        if (!validateStep1()) return false
        if (targetStep <= 2) return true
        return validateStep2()
    }

    const validateCurrentStep = (): boolean => {
        if (currentStep === 1) return validateStep1()
        if (currentStep === 2) return validateStep2()
        return validateStep3()
    }

    const markStepsCompletedUpTo = (step: number) => {
        setCompletedSteps((prev) => {
            const next = new Set(prev)
            for (let i = 1; i < step; i += 1) {
                next.add(i)
            }
            return next
        })
    }

    const clearCompletedAfter = (step: number) => {
        setCompletedSteps((prev) => {
            const next = new Set(prev)
            for (let i = step + 1; i <= WIZARD_STEPS.length; i += 1) {
                next.delete(i)
            }
            return next
        })
    }

    const goToStep = (step: 1 | 2 | 3) => {
        if (isAgencyAgentFlow && step !== 3) return
        if (step === currentStep) return

        if (step < currentStep) {
            setCurrentStep(step)
            clearCompletedAfter(step)
            setCompletedSteps((prev) => {
                const next = new Set(prev)
                next.delete(step)
                return next
            })
            return
        }

        if (VALIDATE_ON_STEPPER_CLICK && !validateUpToStep(step)) return

        markStepsCompletedUpTo(step)
        setCurrentStep(step)
        if (step === 3) {
            enterStep3()
        }
    }

    const handleNext = () => {
        debugger
        if (!validateCurrentStep()) return

        if (currentStep === 3) {
            void handleSave()
            return
        }

        markStepsCompletedUpTo(currentStep + 1)
        const nextStep = (currentStep + 1) as 2 | 3
        setCurrentStep(nextStep)
        if (nextStep === 2) {
            setExpandedOfficeIndexes(new Set([0]))
        }
        if (nextStep === 3) {
            enterStep3()
        }
    }

    const handleBack = () => {
        if (isAgencyAgentFlow || currentStep === 1) {
            onClose()
            return
        }
        const previousStep = (currentStep - 1) as 1 | 2
        setCurrentStep(previousStep)
        clearCompletedAfter(previousStep)
    }

    const updateOffice = (index: number, field: OfficeFieldKey, value: string) => {
        setOffices((prev) => {
            const updated = [...prev]
            updated[index] = { ...updated[index], [field]: value }
            return updated
        })
        if (index === 0 && (field === "contact_number" || field === "email")) {
            patchPrimaryAgent({ [field]: value })
        }
    }

    const handleContactPersonNameChange = (value: string) => {
        setContactPersonName(value)
        patchPrimaryAgent({ agent_name: value })
    }

    const handleAgencyNameChange = (value: string) => {
        setAgencyName(value)
        if (isEditAgencyMode) return

        setOffices((prev) => {
            if (!prev[0]) return prev
            const updated = [...prev]
            updated[0] = { ...updated[0], office_name: value }
            return updated
        })
    }

    const addOffice = () => {
        setOffices((prev) => [...prev, emptyOffice()])
        setAgents((prev) => [...prev, [emptyAgent()]])
        setExpandedOfficeIndexes(new Set([offices.length]))
    }

    const removeOffice = (index: number) => {
        if (index === 0) return
        setOffices((prev) => prev.filter((_, i) => i !== index))
        setAgents((prev) => prev.filter((_, i) => i !== index))
        setExpandedOfficeIndexes((prev) => {
            const next = new Set<number>()
            prev.forEach((value) => {
                if (value === index) return
                next.add(value > index ? value - 1 : value)
            })
            if (next.size === 0) next.add(0)
            return next
        })
    }

    const updateAgent = (officeIndex: number, agentIndex: number, field: keyof AgentFormData, value: string) => {
        setAgents((prev) => {
            const updated = prev.map((officeAgents) => [...officeAgents])
            if (!updated[officeIndex]) updated[officeIndex] = [emptyAgent()]
            updated[officeIndex][agentIndex] = { ...updated[officeIndex][agentIndex], [field]: value }
            return updated
        })

        // First agent is the agency contact: mirror its edits back to step 1 / Office 1.
        if (!isEditFlow || !isPrimaryAgentRow(agents[officeIndex]?.[agentIndex])) return
        if (field === "agent_name") {
            setContactPersonName(value)
        } else if (field === "email" || field === "contact_number") {
            setOffices((prev) => {
                if (!prev[0]) return prev
                const updated = [...prev]
                updated[0] = { ...updated[0], [field]: value }
                return updated
            })
        }
    }

    const addAgentToOffice = (officeIndex: number) => {
        setAgents((prev) => {
            const updated = prev.map((officeAgents) => [...officeAgents])
            if (!updated[officeIndex]) updated[officeIndex] = []
            updated[officeIndex].push(emptyAgent())
            return updated
        })
    }

    const removeAgentFromOffice = (officeIndex: number, agentIndex: number) => {
        if (isEditAgentMode) return
        const agentRow = agents[officeIndex]?.[agentIndex]
        if (isExistingAgentRow(agentRow)) return
        if (!isAddAgentsMode && !isEditAgencyMode && officeIndex === 0 && agentIndex === 0) return
        const officeAgents = agents[officeIndex] ?? []
        if (officeAgents.length <= 1) return
        setAgents((prev) => {
            const updated = prev.map((rows) => [...rows])
            updated[officeIndex] = updated[officeIndex].filter((_, i) => i !== agentIndex)
            return updated
        })
    }

    const toggleOfficeAccordion = (index: number) => {
        setExpandedOfficeIndexes((prev) => {
            const next = new Set(prev)
            if (next.has(index)) next.delete(index)
            else next.add(index)
            return next
        })
    }

    const buildPayload = () => ({
        agency_name: agencyName.trim(),
        contact_person_name: contactPersonName.trim(),
        phone_number: offices[0]?.contact_number?.trim() || phoneNumber.trim(),
        email: offices[0]?.email?.trim() || email.trim(),
        office: activeOfficeIndexes.map((officeIndex) => ({
            office_name: offices[officeIndex].office_name.trim(),
            city_name: offices[officeIndex].city_name.trim(),
            address: offices[officeIndex].address.trim(),
            contact_number: offices[officeIndex].contact_number.trim() || null,
            email: offices[officeIndex].email.trim() || null,
            flag_active: true,
            agents: (agents[officeIndex] ?? []).map((agentRow) => ({
                agent_name: agentRow.agent_name.trim(),
                email: agentRow.email.trim() || null,
                contact_number: agentRow.contact_number.trim(),
                flag_active: true,
            })),
        })),
    })

    const handleSave = async () => {
        if (!validateStep3()) return
        setIsSaving(true)
        setErrors({})
        try {
            if (isEditFlow) {
                if (!agency?.agency_id) {
                    showError(isEditAgentMode ? "Agent not found" : "Agency not found")
                    return
                }

                const response = await agent.AgencyUpsertAPI.post(
                    buildUpsertPayload({
                        agencyId: agency.agency_id,
                        agencyName,
                        contactPersonName,
                        phoneNumber,
                        email,
                        flagActive,
                        offices,
                        agents,
                    }),
                )

                if (response?.status === true) {
                    showSuccess(isEditAgentMode ? "Agent updated successfully" : "Agency updated successfully")
                    onSuccess?.()
                    onClose()
                } else {
                    showError(
                        response?.error?.message ??
                            (isEditAgentMode ? "Failed to update agent" : "Failed to update agency"),
                    )
                }
                return
            }

            if (isAddAgentsMode) {
                if (!agency?.agency_id) {
                    showError("Agency not found")
                    return
                }

                const agentRequests: Promise<any>[] = []
                activeOfficeIndexes.forEach((officeIndex) => {
                    const office = offices[officeIndex]
                    if (!office?.office_id) return

                    ;(agents[officeIndex] ?? []).forEach((agentRow) => {
                        if (isExistingAgentRow(agentRow) || isAgentRowEmpty(agentRow)) return
                        agentRequests.push(
                            agent.AgentPutAPI.put({
                                agent_id: null,
                                agent_name: agentRow.agent_name.trim(),
                                email: agentRow.email.trim() || null,
                                contact_number: agentRow.contact_number.trim() || null,
                                agency_id: agency.agency_id,
                                office_id: office.office_id as string,
                                flag_active: true,
                            }),
                        )
                    })
                })

                if (agentRequests.length === 0) {
                    showError("Please add at least one agent")
                    return
                }

                const results = await Promise.all(agentRequests)
                const failed = results.find((response) => response?.status !== true)
                if (failed) {
                    showError(failed?.error?.message ?? "Failed to add agent")
                    return
                }

                showSuccess(
                    agentRequests.length === 1 ? "Agent added successfully" : "Agents added successfully",
                )
                onSuccess?.()
                onClose()
                return
            }

            const response = await agent.AgencyOnboardAPI.post(buildPayload())
            if (response?.status === true) {
                showSuccess("Agency added successfully")
                onSuccess?.()
                onClose()
            } else {
                showError(response?.error?.message ?? "Failed to add agency")
            }
        } catch (err) {
            console.error("Agency save error", err)
            showError(
                isEditAgencyMode
                    ? "Failed to update agency"
                    : isEditAgentMode
                      ? "Failed to update agent"
                      : isAddAgentsMode
                        ? "Failed to add agent"
                        : "Failed to add agency",
            )
        } finally {
            setIsSaving(false)
        }
    }

    const renderOfficeFields = (officeIndex: number) => {
        const office = offices[officeIndex]
        const officeErr = errors.officeErrors?.[officeIndex] ?? {}

        return (
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <InputText
                        label="Office Name"
                        value={office.office_name}
                        onChange={(e) => updateOffice(officeIndex, "office_name", e.target.value)}
                        placeholder="Enter office name"
                        required
                        error={officeErr.office_name}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <InputText
                        label="City"
                        value={office.city_name}
                        onChange={(e) => updateOffice(officeIndex, "city_name", e.target.value)}
                        placeholder="Enter city"
                        required
                        error={officeErr.city_name}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <InputText
                        label="Address"
                        value={office.address}
                        onChange={(e) => updateOffice(officeIndex, "address", e.target.value)}
                        placeholder="Enter office address"
                        required
                        error={officeErr.address}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <PhoneInput
                        label="Contact Number"
                        value={office.contact_number}
                        onChange={(value) => updateOffice(officeIndex, "contact_number", value)}
                        placeholder="Enter contact number"
                        required
                        international={true}
                        defaultCountry="IN"
                        error={officeErr.contact_number}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <InputText
                        label="Email ID"
                        type="email"
                        value={office.email}
                        onChange={(e) => updateOffice(officeIndex, "email", e.target.value)}
                        placeholder="office@agency.com"
                        required
                        error={officeErr.email}
                    />
                </Grid>
            </Grid>
        )
    }

    const renderStep1 = () => {
        if (isEditAgencyMode && (isLoadingAgencyData || offices.length === 0)) {
            return <p className={styles.loadingText}>Loading agency data...</p>
        }

        const officeErr = errors.officeErrors?.[0] ?? {}

        return (
            <>
                <h3 className={styles.sectionHeading}>Agency Information</h3>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <InputText
                            label="Agency Name"
                            value={agencyName}
                            onChange={(e) => handleAgencyNameChange(e.target.value)}
                            placeholder="Enter agency name"
                            required
                            error={errors.agencyName}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <InputText
                            label="Contact Person Name"
                            value={contactPersonName}
                            onChange={(e) => {
                                handleContactPersonNameChange(e.target.value);
                                console.log("Printing value of agents", agents)
                            }}
                            placeholder="Enter contact person name"
                            required
                            error={errors.contactPersonName}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <InputText
                            label="City"
                            value={offices[0].city_name}
                            onChange={(e) => updateOffice(0, "city_name", e.target.value)}
                            placeholder="Enter city"
                            required
                            error={officeErr.city_name}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <InputText
                            label="Address"
                            value={offices[0].address}
                            onChange={(e) => updateOffice(0, "address", e.target.value)}
                            placeholder="Enter office address"
                            required
                            error={officeErr.address}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <PhoneInput
                            label="Contact Number"
                            value={offices[0].contact_number}
                            onChange={(value) => updateOffice(0, "contact_number", value)}
                            placeholder="Enter contact number"
                            required
                            international={true}
                            defaultCountry="IN"
                            error={officeErr.contact_number}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <InputText
                            label="Email ID"
                            type="email"
                            value={offices[0].email}
                            onChange={(e) => updateOffice(0, "email", e.target.value)}
                            placeholder="office@agency.com"
                            required
                            error={officeErr.email}
                        />
                    </Grid>
                </Grid>

                <div className={styles.sectionSpacer} />

                <Grid container spacing={2}>
                    {/* <Grid size={{ xs: 12, md: 6 }}>
                        <PhoneInput
                            label="Phone Number"
                            value={phoneNumber}
                            onChange={(value) => setPhoneNumber(value)}
                            placeholder="Enter phone number"
                            required
                            error={errors.phoneNumber}
                            international={true}
                            defaultCountry="IN"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <InputText
                            label="Email ID"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="contact@agency.com"
                            required
                            error={errors.email}
                        />
                    </Grid> */}
                    <Grid size={{ xs: 12 }}>
                        <div className={styles.statusRow}>
                            <span className={styles.statusLabel}>Status</span>
                            <Toggle checked={flagActive} onChange={setFlagActive} rightLabel="Active" />
                        </div>
                    </Grid>
                </Grid>
            </>
        )
    }

    const renderStep2 = () => (
        <>
            {isEditAgencyMode && isLoadingAgencyData ? (
                <p className={styles.loadingText}>Loading agency data...</p>
            ) : (
            <div className={styles.accordionList}>
                {offices.map((office, index) => {
                    const isExpanded = expandedOfficeIndexes.has(index)
                    const agentCountLabel =
                        index === 0
                            ? "Pre-filled from agency details"
                            : getOfficeFillState(office) === "empty"
                              ? "Optional — fill all fields to include"
                              : "Ready to save"

                    return (
                        <div key={index} className={styles.accordionItem}>
                            <button
                                type="button"
                                className={styles.accordionHeader}
                                onClick={() => toggleOfficeAccordion(index)}
                            >
                                <div className={styles.accordionHeaderLeft}>
                                    <IconsColor
                                        iconName="location"
                                        width="18"
                                        height="18"
                                        fillColor="none"
                                        strokeColor="#195893"
                                    />
                                    <span className={styles.accordionTitle}>Office {index + 1}</span>
                                    <span className={styles.accordionMeta}>
                                        {office.office_name?.trim() ? `(${office.office_name})` : `(${agentCountLabel})`}
                                    </span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    {index > 0 && (
                                        <div
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                removeOffice(index)
                                            }}
                                            style={{ cursor: "pointer", display: "flex" }}
                                            title="Remove office"
                                        >
                                            <IconsColor
                                                iconName="deleteIcon"
                                                width="18"
                                                height="18"
                                                fillColor="none"
                                                strokeColor="#F44336"
                                            />
                                        </div>
                                    )}
                                    <span
                                        className={`${styles.chevronIcon} ${isExpanded ? styles.chevronExpanded : ""}`}
                                    >
                                        <IconsColor
                                            iconName="chevronDown"
                                            width="18"
                                            height="18"
                                            fillColor="none"
                                            strokeColor="#616161"
                                        />
                                    </span>
                                </div>
                            </button>
                            {isExpanded && <div className={styles.accordionBody}>{renderOfficeFields(index)}</div>}
                        </div>
                    )
                })}
            </div>
            )}
            {!isEditAgencyMode || !isLoadingAgencyData ? (
            <span className={styles.addOfficeLink} onClick={addOffice}>
                + Add New Office
            </span>
            ) : null}
        </>
    )

    const renderStep3 = () => (
        <>
            {isExistingAgencyFlow && agencyName && (
                <div className={styles.agencySummary}>
                    <span className={styles.agencySummaryLabel}>Agency</span>
                    <span className={styles.agencySummaryValue}>{agencyName}</span>
                </div>
            )}

            <div className={styles.infoBanner}>
                <IconsColor iconName="warningIcon" width="18" height="18" fillColor="none" strokeColor="#195893" />
                <span>
                    Total: <strong>{totalAgentCount}</strong> agent{totalAgentCount === 1 ? "" : "s"} added across{" "}
                    <strong>{activeOfficeIndexes.length}</strong> office{activeOfficeIndexes.length === 1 ? "" : "s"}
                </span>
            </div>

            <h3 className={styles.agentSectionTitle}>Assign Agents to Offices</h3>

            {isLoadingAgencyData ? (
                <p className={styles.loadingText}>Loading agency data...</p>
            ) : (
            <div className={styles.accordionList}>
                {activeOfficeIndexes.map((officeIndex) => {
                    const office = offices[officeIndex]
                    console.log("Printing value of officeIndex and activeOfficeIndexes", officeIndex, activeOfficeIndexes)
                    const officeAgents = agents[officeIndex] ?? [emptyAgent()]
                    console.log("Printing value of officeAgents", officeAgents)
                    const isExpanded = expandedOfficeIndexes.has(officeIndex)
                    const officeLabel = office.office_name?.trim() || `Office ${officeIndex + 1}`

                    return (
                        <div key={officeIndex} className={styles.accordionItem}>
                            <button
                                type="button"
                                className={styles.accordionHeader}
                                onClick={() => toggleOfficeAccordion(officeIndex)}
                            >
                                <div className={styles.accordionHeaderLeft}>
                                    <IconsColor
                                        iconName="location"
                                        width="18"
                                        height="18"
                                        fillColor="none"
                                        strokeColor="#195893"
                                    />
                                    <span className={styles.accordionTitle}>{officeLabel}</span>
                                    <span className={styles.accordionMeta}>
                                        ({countDisplayAgents(officeAgents)} agent
                                        {countDisplayAgents(officeAgents) === 1 ? "" : "s"})
                                    </span>
                                </div>
                                <span
                                    className={`${styles.chevronIcon} ${isExpanded ? styles.chevronExpanded : ""}`}
                                >
                                    <IconsColor
                                        iconName="chevronDown"
                                        width="18"
                                        height="18"
                                        fillColor="none"
                                        strokeColor="#616161"
                                    />
                                </span>
                            </button>
                            {isExpanded && (
                                <div className={styles.accordionBody}>
                                    {officeAgents.map((agentRow, agentIndex) => {
                                        const agentErr = errors.agentsErrors?.[officeIndex]?.[agentIndex] ?? {}
                                        const isExistingAgent = isExistingAgentRow(agentRow)
                                        const isPrimaryAgentInCreate =
                                            !isAgencyAgentFlow && officeIndex === 0 && agentIndex === 0
                                        const isEditingThisAgent =
                                            isEditAgentMode &&
                                            Boolean(editingAgent?.agent_id) &&
                                            agentRow.agent_id === editingAgent?.agent_id
                                        const isFieldDisabled = isEditAgentMode
                                            ? !isEditingThisAgent
                                            : isAddAgentsMode
                                              ? isExistingAgent
                                              : false
                                        const canDelete =
                                            !isEditAgentMode &&
                                            !isExistingAgent &&
                                            officeAgents.length > 1 &&
                                            !isPrimaryAgentInCreate
                                        const requireContactFields =
                                            isAgentContactFieldsRequired(agentIndex) || isPrimaryAgentRow(agentRow)

                                        return (
                                            <div
                                                key={agentRow.agent_id ?? `new-${officeIndex}-${agentIndex}`}
                                                className={styles.agentRow}
                                            >
                                                <div className={styles.agentFields}>
                                                    <Grid container spacing={2}>
                                                        <Grid size={{ xs: 12, md: 4 }}>
                                                            <InputText
                                                                label="Agent Name"
                                                                value={agentRow.agent_name}
                                                                onChange={(e) =>
                                                                    updateAgent(
                                                                        officeIndex,
                                                                        agentIndex,
                                                                        "agent_name",
                                                                        e.target.value,
                                                                    )
                                                                }
                                                                placeholder="Enter agent name"
                                                                required
                                                                error={agentErr.agent_name}
                                                                disabled={isFieldDisabled}
                                                            />
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 4 }}>
                                                            <InputText
                                                                label="Email ID"
                                                                type="email"
                                                                value={agentRow.email}
                                                                onChange={(e) =>
                                                                    updateAgent(
                                                                        officeIndex,
                                                                        agentIndex,
                                                                        "email",
                                                                        e.target.value,
                                                                    )
                                                                }
                                                                placeholder="agent@agency.com"
                                                                required={requireContactFields}
                                                                error={agentErr.email}
                                                                disabled={isFieldDisabled}
                                                            />
                                                        </Grid>
                                                        <Grid size={{ xs: 12, md: 4 }}>
                                                            <PhoneInput
                                                                label="Phone Number"
                                                                value={agentRow.contact_number}
                                                                onChange={(value) =>
                                                                    updateAgent(
                                                                        officeIndex,
                                                                        agentIndex,
                                                                        "contact_number",
                                                                        value,
                                                                    )
                                                                }
                                                                placeholder="Enter phone number"
                                                                required={requireContactFields}
                                                                international={true}
                                                                defaultCountry="IN"
                                                                error={agentErr.contact_number}
                                                                disabled={isFieldDisabled}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </div>
                                                <div
                                                    className={`${styles.agentDeleteButton} ${canDelete ? "" : styles.disabled}`}
                                                    onClick={() => {
                                                        if (canDelete) removeAgentFromOffice(officeIndex, agentIndex)
                                                    }}
                                                    title={
                                                        isEditAgentMode
                                                            ? "Agent cannot be removed in edit mode"
                                                            : isExistingAgent
                                                              ? "Existing agent cannot be removed here"
                                                              : isPrimaryAgentInCreate
                                                                ? "Primary agent from step 1 cannot be removed"
                                                                : canDelete
                                                                  ? "Remove agent"
                                                                  : "At least one agent is required per office"
                                                    }
                                                >
                                                    <IconsColor
                                                        iconName="deleteIcon"
                                                        width="20"
                                                        height="20"
                                                        fillColor="none"
                                                        strokeColor="#F44336"
                                                    />
                                                </div>
                                            </div>
                                        )
                                    })}
                                    {!isEditAgentMode && (
                                        <span
                                            className={styles.addAgentLink}
                                            onClick={() => addAgentToOffice(officeIndex)}
                                        >
                                            + Add Agent
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
            )}
        </>
    )

    const popupTitle = isEditAgencyMode
        ? agencyName
            ? `Edit Agency - ${agencyName}`
            : "Edit Agency"
        : isEditAgentMode
          ? editingAgent?.agent_name
              ? `Edit Agent - ${editingAgent.agent_name}`
              : agencyName
                ? `Edit Agent - ${agencyName}`
                : "Edit Agent"
          : isAddAgentsMode
            ? agencyName
                ? `Add Agent - ${agencyName}`
                : "Add Agent"
            : "Add Agency"
    const secondaryLabel = isAgencyAgentFlow || currentStep === 1 ? "Cancel" : "← Back"
    const primaryLabel = isEditAgencyMode
        ? isSaving
            ? "Saving..."
            : "Save Changes"
        : isEditAgentMode
          ? isSaving
              ? "Saving..."
              : "Save Changes"
          : isAddAgentsMode
            ? isSaving
                ? "Saving..."
                : "Save Agent"
            : currentStep === 1
              ? "Next: Add Offices →"
              : currentStep === 2
                ? "Next: Add Agents →"
                : isSaving
                  ? "Saving..."
                  : "Save Agency"

    return (
        <Popup isOpen={isOpen} onClose={onClose} title={popupTitle} size="large" heightFiexd={true}>
            <div className={styles.wizardBody}>
                <div className={styles.wizardStepper}>
                    <AgencyWizardStepper
                        steps={WIZARD_STEPS}
                        currentStep={currentStep}
                        completedSteps={completedSteps}
                        onStepClick={(step) => goToStep(step as 1 | 2 | 3)}
                    />
                </div>

                <div className={styles.stepContent}>
                    {currentStep === 1 && renderStep1()}
                    {currentStep === 2 && renderStep2()}
                    {currentStep === 3 && renderStep3()}
                </div>

                <div className={styles.wizardFooter}>
                    <button type="button" className={styles.footerTextButton} onClick={handleBack} disabled={isSaving}>
                        {secondaryLabel}
                    </button>
                    <Button
                        type="contained"
                        label={primaryLabel}
                        onClick={handleNext}
                        disabled={isSaving}
                    />
                </div>
            </div>
        </Popup>
    )
}

export default AddAgencyWizardPopup
